"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import Image from "next/image";

interface LogosCarouselProps {
  logos: { src: string; link: string }[];
}

const LogosCarousel: React.FC<LogosCarouselProps> = ({ logos }) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [sliderWidth, setSliderWidth] = useState(0);

  const x = useMotionValue(0);
  const xLoop = useTransform(
    x,
    (value) => -(((value % sliderWidth) + sliderWidth) % sliderWidth)
  );

  const duration = 18000;

  useEffect(() => {
    if (sliderRef.current) {
      setSliderWidth(sliderRef.current.scrollWidth / 2);
    }
  }, [logos]);

  useEffect(() => {
    if (!sliderWidth) return;
    let animationFrame: number;
    let previousTime = performance.now();
    const speed = sliderWidth / duration;

    const update = (time: number) => {
      const delta = time - previousTime;
      previousTime = time;
      x.set(x.get() + speed * delta);
      animationFrame = requestAnimationFrame(update);
    };

    animationFrame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animationFrame);
  }, [sliderWidth, x, duration]);

  return (
    <div className="w-full relative overflow-visible ">
      <motion.div
        ref={sliderRef}
        className="flex"
        style={{ x: xLoop, width: "max-content", willChange: "transform" }}
      >
        {[...logos, ...logos].map(({ src, link }, index) => (
          <a
            key={index}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="group px-[10px] relative flex flex-col items-center"
          >
            <motion.div
              className="relative"
              whileHover={{ scale: 1.5, zIndex: 50 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <Image
                src={src}
                alt="logo"
                width={90}
                height={50}
                unoptimized
                className="w-fit min-w-[90px] object-contain h-[50px] md:h-[90px]"
              />
            </motion.div>
          </a>
        ))}
      </motion.div>
    </div>
  );
};

export default LogosCarousel;
